import { useCallback, useState } from 'react';

export const SIDEBAR_EXPAND_STATE_KEY = 'platform.sidebar.expandedItems';

type ExpandMap = Record<string, boolean>;

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const readFromStorage = (): ExpandMap => {
  try {
    const saved = localStorage.getItem(SIDEBAR_EXPAND_STATE_KEY);
    if (!saved) return {};
    const parsed: unknown = JSON.parse(saved);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        ([k, v]) => !UNSAFE_KEYS.has(k) && typeof v === 'boolean',
      ),
    ) as ExpandMap;
  } catch {
    return {};
  }
};

const writeToStorage = (map: ExpandMap): void => {
  try {
    localStorage.setItem(SIDEBAR_EXPAND_STATE_KEY, JSON.stringify(map));
  } catch {
    // localStorage can throw under Safari Private Mode / quota — ignore.
  }
};

export const useSidebarExpandState = () => {
  const [expandMap, setExpandMap] = useState<ExpandMap>(readFromStorage);

  const isOpen = useCallback(
    (key: string, defaultOpen?: boolean): boolean => {
      if (!UNSAFE_KEYS.has(key) && Object.prototype.hasOwnProperty.call(expandMap, key)) return expandMap[key];
      return defaultOpen ?? false;
    },
    [expandMap],
  );

  const setOpen = useCallback((key: string, open: boolean): void => {
    if (UNSAFE_KEYS.has(key)) return;
    setExpandMap((prev) => {
      const next = { ...prev, [key]: open };
      writeToStorage(next);
      return next;
    });
  }, []);

  return { isOpen, setOpen };
};
